import { ref, type Ref, onMounted } from 'vue';
import { doc, getDoc, type FirestoreError } from 'firebase/firestore';
import { useFirestore } from 'vuefire';
import type { TicketStatus } from '@wowpress/types';

// 챗봇 설정 데이터 구조 정의
interface ChatbotSettings {
    avatarUrl?: string;
    description?: string;
    name?: string;
}

interface TicketAutoCloseSettings {
    enabled: boolean;
    expireAfter: number;
    targetStatus: TicketStatus;
}

// --- 전역 상태 --- (모듈 스코프)
const chatbot: Ref<ChatbotSettings | null> = ref(null);
const autoClose: Ref<TicketAutoCloseSettings | null> = ref(null);
const loading: Ref<boolean> = ref(false); // 초기 로딩 상태는 false로 변경, fetch 시 true
const error: Ref<FirestoreError | null> = ref(null);
let isFetchInitiated = false; // 데이터 로딩 시도 여부 플래그

// --- 데이터를 한 번 가져오는 함수 (내부 로직) ---
async function fetchSettingsOnce(db: ReturnType<typeof useFirestore>) {
    if (!db) {
        console.error('[useSetting] Firestore 인스턴스를 사용할 수 없습니다.');
        error.value = new Error('Firestore 인스턴스를 사용할 수 없습니다.') as FirestoreError;
        return;
    }

    // 이미 로딩 중이거나 데이터가 있으면 중복 실행 방지 (더 확실하게)
    if (loading.value || (chatbot.value !== null && autoClose.value !== null)) return;

    loading.value = true;
    error.value = null;
    isFetchInitiated = true;

    try {
        const settingDocRef = doc(db, 'settings', 'ai');
        const docSnap = await getDoc(settingDocRef);

        if (docSnap.exists()) {
            const data = docSnap.data();

            // autoClose 설정 로딩
            if (data && typeof data.ticketAutoClose === 'object' && data.ticketAutoClose !== null &&
                typeof data.ticketAutoClose.enabled === 'boolean' &&
                typeof data.ticketAutoClose.expireAfter === 'number' &&
                typeof data.ticketAutoClose.targetStatus === 'string') {
                autoClose.value = {
                    enabled: data.ticketAutoClose.enabled,
                    expireAfter: data.ticketAutoClose.expireAfter, // 초 단위로 가정
                    targetStatus: data.ticketAutoClose.targetStatus as TicketStatus,
                };
            } else {
                 autoClose.value = null; // 형식이 맞지 않거나 필드가 없으면 null 설정
                 console.warn("'/settings/ai' 문서에 'ticketAutoClose' 필드가 없거나 형식이 올바르지 않습니다.");
            }

            // chatbot 설정 로딩
            if (data && typeof data.chatbot === 'object' && data.chatbot !== null) {
                chatbot.value = {
                    avatarUrl: data.chatbot.avatarUrl,
                    description: data.chatbot.description,
                    name: data.chatbot.name,
                };
            } else {
                chatbot.value = {};
                console.warn("'/settings/ai' 문서에 'chatbot' 필드가 없거나 형식이 올바르지 않습니다.");
            }
        } else {
            chatbot.value = null;
            autoClose.value = null; // 문서가 없으면 둘 다 null
            error.value = new Error("'/settings/ai' 문서를 찾을 수 없습니다.") as FirestoreError;
            console.error("'/settings/ai' 문서를 찾을 수 없습니다.");
        }
    } catch (err: any) {
        console.error('[useSetting] Error fetching settings:', err);
        error.value = err;
        chatbot.value = null;
        autoClose.value = null; // 에러 시 둘 다 null
    } finally {
        loading.value = false;
    }
}

// --- 컴포저블 함수 --- (전역 상태와 인터페이스 제공)
export function useSetting() {


    // 수동 리프레시 함수
    const fetchSettings = () => {
        const db = useFirestore();

        // 이미 로딩중이면 무시
        if (loading.value) return;
        // 수동 호출 시에는 isFetchInitiated와 무관하게 로딩 시도
        chatbot.value = null; // 데이터를 새로 받기 위해 초기화
        autoClose.value = null; // autoClose도 초기화
        isFetchInitiated = false; // 다시 로딩 가능하도록 플래그 초기화 (선택적)
        fetchSettingsOnce(db);
    }

    // 전역 상태 변수 및 수동 리프레시 함수 반환
    return {
        chatbot,
        autoClose,
        loading,
        error,
        fetchSettings, // 수동으로 데이터를 다시 가져올 수 있도록 함수 노출
    };
}
